/**
 * Tech 主题顶部导航菜单组件
 * 图标 + 文字导航，激活态发光背景框
 */
import React, { useMemo, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Dropdown } from 'antd'
import {
  HomeOutlined,
  ApartmentOutlined,
  GlobalOutlined,
  FundProjectionScreenOutlined,
  ControlOutlined,
  AlertOutlined,
  AimOutlined,
  BorderOuterOutlined,
  AppstoreOutlined,
  DownOutlined,
} from '@ant-design/icons'
import { useAuthStore } from '@/store/authStore'
import { useLanguage } from '@/hooks/useLanguage'
import styles from './TechLayout.module.less'
import { getActiveDropdownKeys, getMenuTitle, isMenuActive, renderDropdownItems } from './topMenuUtils'

const iconMap: Record<string, React.ReactNode> = {
  '/': <HomeOutlined />,
  '/basic': <ApartmentOutlined />,
  '/map': <GlobalOutlined />,
  '/decision': <FundProjectionScreenOutlined />,
  '/system': <ControlOutlined />,
  '/disease': <AlertOutlined />,
  '/patrol': <AimOutlined />,
  '/draw': <BorderOuterOutlined />,
}

const TechTopMenu: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { menus } = useAuthStore()
  const { isEnUS } = useLanguage()
  const [openKey, setOpenKey] = useState<string | null>(null)

  const topMenuItems = useMemo(() => {
    if (!menus || menus.length === 0) return []

    const topMenus = menus
      .filter(m => m.is_show !== 0 && m.url && m.url !== '/login')
      .sort((a, b) => a.sort - b.sort)

    return topMenus.map(menu => {
      const menuPath = menu.url === '/' ? '' : menu.url
      const fullPath = menuPath.startsWith('/') ? menuPath : `/${menuPath}`
      const hasChildren = menu.child?.some(c => c.is_show !== 0)
      const isActive = isMenuActive(location.pathname, fullPath)
      const icon = iconMap[fullPath] || <AppstoreOutlined />
      const dropdownItems = hasChildren
        ? renderDropdownItems(menu, fullPath, navigate, isEnUS)
        : undefined
      const selectedKeys = dropdownItems
        ? getActiveDropdownKeys(dropdownItems, location.pathname)
        : undefined

      return { key: menu.url, fullPath, label: getMenuTitle(menu, isEnUS), icon, isActive, hasChildren, dropdownItems, selectedKeys }
    })
  }, [menus, navigate, location.pathname, isEnUS])

  return (
    <div className={styles.topNavScroll}>
      <div className={`${styles.topNavList} ${isEnUS ? styles.topNavListCompact : ''}`}>
        {topMenuItems.map(item => {
          const inner = (
            <div
              className={`${styles.topNavItem} ${isEnUS ? styles.topNavItemCompact : ''} ${item.isActive ? styles.topNavItemActive : ''} ${openKey === item.key ? styles.topNavItemOpen : ''}`}
              onClick={!item.hasChildren ? () => navigate(item.fullPath) : undefined}
              title={item.label}
            >
              <span className={`${styles.topNavIcon} ${isEnUS ? styles.topNavIconCompact : ''}`}>{item.icon}</span>
              <span className={styles.topNavText}>{item.label}</span>
              {item.hasChildren && <DownOutlined className={`${styles.topNavArrow} ${isEnUS ? styles.topNavArrowCompact : ''}`} />}
            </div>
          )

          if (item.dropdownItems && item.dropdownItems.length > 0) {
            return (
              <Dropdown
                key={item.key}
                menu={{
                  items: item.dropdownItems,
                  selectedKeys: item.selectedKeys,
                  selectable: true,
                  onClick: () => setOpenKey(null),
                }}
                overlayClassName="top-nav-dropdown"
                overlayStyle={{ zIndex: 1300 }}
                open={openKey === item.key}
                onOpenChange={(open) => setOpenKey(open ? item.key : null)}
                placement="bottomLeft"
                trigger={['click']}
              >
                <div className={styles.topNavNode}>{inner}</div>
              </Dropdown>
            )
          }
          return (
            <div key={item.key} className={styles.topNavNode}>
              {inner}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default TechTopMenu
